import { useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/cn";
import { mapOutcomeToLevel } from "@/lib/format";
import { strings } from "@/lib/strings";
import type { WizardOperation } from "@/types/wizard";

interface DataTableProps {
  operations: WizardOperation[];
  emptyLabel?: string;
  filterable?: boolean;
}

export function DataTable({
  operations,
  emptyLabel = strings.table.noOperations,
  filterable = true,
}: DataTableProps) {
  const [outcomeFilter, setOutcomeFilter] = useState<string>("");

  const outcomes = useMemo(() => {
    const set = new Set<string>();
    operations.forEach((op) => set.add(op.outcome));
    return Array.from(set).sort();
  }, [operations]);

  const filtered = useMemo(() => {
    if (!outcomeFilter) return operations;
    return operations.filter((op) => op.outcome === outcomeFilter);
  }, [operations, outcomeFilter]);

  if (!operations.length) {
    return (
      <div className="rounded-lg border border-dashed border-border/60 bg-card/30 px-4 py-8 text-center text-sm text-muted-foreground">
        {emptyLabel}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {filterable && outcomes.length > 1 ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">{strings.table.filterOutcome}:</span>
          <button
            type="button"
            onClick={() => setOutcomeFilter("")}
            className={cn(
              "rounded-md border px-2 py-0.5 text-xs font-mono transition-colors",
              !outcomeFilter ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground",
            )}
          >
            {strings.table.allOutcomes}
          </button>
          {outcomes.map((outcome) => (
            <button
              key={outcome}
              type="button"
              onClick={() => setOutcomeFilter(outcome)}
              className={cn(
                "rounded-md border px-2 py-0.5 text-xs font-mono uppercase transition-colors",
                outcomeFilter === outcome
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-border text-muted-foreground",
              )}
            >
              {outcome}
            </button>
          ))}
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-border/60">
        <table className="data-table w-full min-w-[640px] text-left text-sm">
          <thead>
            <tr className="border-b border-border/60 bg-muted/30 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-2 font-mono">{strings.table.timestamp}</th>
              <th className="px-3 py-2 font-mono">{strings.table.stage}</th>
              <th className="px-3 py-2 font-mono">{strings.table.outcome}</th>
              <th className="px-3 py-2">{strings.table.message}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((op, index) => {
              const level = mapOutcomeToLevel(op.outcome);
              return (
                <tr key={`${op.stage}-${op.timestamp}-${index}`} className="border-b border-border/40 last:border-0">
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-muted-foreground mono-num">
                    {new Date(op.timestamp).toLocaleString()}
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className="font-mono text-[10px] uppercase">
                      {op.stage}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">
                    <Badge
                      variant={
                        level === "success"
                          ? "success"
                          : level === "warn"
                            ? "warning"
                            : level === "error"
                              ? "destructive"
                              : "muted"
                      }
                      className="font-mono text-[10px] uppercase"
                    >
                      {op.outcome}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-foreground/90">{op.message}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
